import { IClassListItem } from "../../../model/respone/classes/IClassListItem";
import { hasScheduleConflict } from "../../../helpers/scheduleConflict";
import { getLopIndex, isMonChungPair, isMonChungIndexMismatch } from "../../../helpers/monChung";
import { buildNhomTuChonUsage, nhomTuChonBlock } from "../../../helpers/nhomTuChon";
import { IClassesReducer } from "../../reducer-model/classes/IClassesReducer";
import { eReducerStatusBase } from "../../reducer-model/eReducerStatusBase";

const initialState: IClassesReducer = {
    status: eReducerStatusBase.is_not_initialization,
    list: [],
    error_message: "",
};

const parseTrungLichCsv = (csv?: string): number[] => {
    if (!csv) return [];
    return csv
        .split(",")
        .map(s => parseInt(s.trim(), 10))
        .filter(n => !isNaN(n) && n > 0);
};

const toCsv = (ids: number[]): string | undefined =>
    ids.length === 0 ? undefined : Array.from(new Set(ids)).join(",");

/** id_mon dùng cho rule 12 (cùng môn): môn GỐC nếu lớp là môn tương đương. Mirror backend idMonForRule. */
const effectiveMonId = (c: IClassListItem): number =>
    (c.id_mon_goc ?? 0) > 0 ? c.id_mon_goc! : c.id_mon;

/**
 * Khoá rule 12: 2 lớp chỉ chặn nhau khi CÙNG môn gốc VÀ cùng phạm vi same_mon_key (backend gửi —
 * AOF null = chặn trong cả đợt; tenant default "{nam_hoc}|{hoc_ky}" = chỉ chặn trong cùng kỳ).
 * Trả "" khi lớp không xác định được môn → caller bỏ qua.
 */
const sameMonKey = (c: IClassListItem): string => {
    const mon = effectiveMonId(c);
    return mon > 0 ? `${mon}|${c.same_mon_key ?? ""}` : "";
};

/**
 * Multi-period: event register/cancel mang periodId của đợt phát sinh. SV có thể đã đổi sang đợt
 * khác trước khi confirm về → optimistic-update sẽ đụng nhầm list đợt mới (đặc biệt khi 2 đợt cùng
 * chứa 1 lớp, vd 2 đợt chia giai đoạn cùng ky_dang_ky). Thiếu 1 trong 2 periodId (Worker/build cũ)
 * → không guard, giữ hành vi cũ.
 */
const isOtherPeriod = (state: IClassesReducer, action: any): boolean => {
    const evtPeriodId: number | undefined = action.payload?.periodId;
    return !!evtPeriodId && !!state.periodId && evtPeriodId !== state.periodId;
};

/** Lớp vừa ĐK có thể là lớp top-level (LT / đơn lẻ) HOẶC lớp thực hành nested trong listThucHanh. */
const findInList = (
    list: IClassListItem[],
    classId: number
): { item: IClassListItem; parentId?: number } | null => {
    for (const p of list) {
        if (p.classId === classId) return { item: p };
        const child = p.listThucHanh?.find(t => t.classId === classId);
        if (child) return { item: child, parentId: p.classId };
    }
    return null;
};

const blockSameMon = (c: IClassListItem): IClassListItem => ({
    ...c,
    canRegister: false,
    reasonCode: "ALREADY_REGISTERED_SAME_MON",
    message: "Đã đăng ký lớp khác cùng môn.",
});

/**
 * Pass optimistic rule 12 (cùng môn) — chạy SAU các nhánh trùng lịch / khoá nhóm trong map.
 * SV vừa ĐK 1 lớp → mọi lớp KHÁC cùng môn trong danh sách bị chặn ngay (fix bug "phải F5 mới ẩn
 * nút Đăng ký / hiện 'Đã ĐK môn này'"). Mirror ClassListService.alreadyRegisteredSameMon.
 * Phạm vi = danh sách đang giữ = lớp của ĐÚNG 1 đợt (CacheKeys.ActiveClasses(periodId)) → không
 * đụng tới lớp cùng môn ở đợt khác (AOF: 1 môn/1 đợt, cross đợt cùng ky_dang_ky vẫn cho ĐK).
 * Chỉ đụng lớp đang eligible — lớp đã chặn vì lý do khác (hết chỗ, ngoài phạm vi, trùng lịch…)
 * giữ nguyên reasonCode gốc.
 * Miễn cặp LT-TH (mirror isParentChildPair backend): lớp LT cha của lớp thực hành vừa ĐK, và các
 * lớp thực hành con của lớp LT vừa ĐK. Lớp thực hành ANH EM (cùng cha, không phải lớp vừa ĐK) vẫn
 * bị chặn — đúng như backend.
 */
const applySameMonBlock = (
    list: IClassListItem[],
    registeredId: number,
    registeredParentId: number | undefined,
    key: string
): IClassListItem[] => {
    if (!key) return list;
    return list.map(p => {
        const isRegistered = p.classId === registeredId;
        const isPairParent = registeredParentId !== undefined && p.classId === registeredParentId;
        let next = !isRegistered && !isPairParent && p.canRegister && sameMonKey(p) === key
            ? blockSameMon(p)
            : p;
        // Lớp thực hành con của lớp vừa ĐK = cặp LT-TH → miễn toàn bộ.
        const children = p.listThucHanh;
        if (children?.length && !isRegistered) {
            const nextChildren = children.map(ch =>
                ch.classId !== registeredId && ch.canRegister && sameMonKey(ch) === key
                    ? blockSameMon(ch)
                    : ch
            );
            if (nextChildren.some((ch, i) => ch !== children[i])) {
                next = { ...next, listThucHanh: nextChildren };
            }
        }
        return next;
    });
};

/**
 * Pass ngược của applySameMonBlock — chạy khi huỷ: mở lại lớp (kể cả lớp thực hành nested) đang bị
 * chặn ALREADY_REGISTERED_SAME_MON của các môn vừa "trống chỗ". Lớp top-level còn trùng lịch với lớp
 * đã ĐK khác thì giữ khoá; lớp thực hành không giữ iD_lops_TrungLich (sub-table tự so lịch tươi).
 */
const restoreSameMon = (list: IClassListItem[], restorableKeys: Set<string>): IClassListItem[] => {
    if (restorableKeys.size === 0) return list;
    const unblock = (c: IClassListItem): IClassListItem =>
        ({ ...c, canRegister: true, reasonCode: undefined, message: undefined });
    const canUnblock = (c: IClassListItem): boolean =>
        c.reasonCode === "ALREADY_REGISTERED_SAME_MON" && restorableKeys.has(sameMonKey(c));
    return list.map(p => {
        // Còn trùng lịch với lớp đã ĐK khác → vẫn khoá, nhưng đổi sang đúng lý do (giữ SAME_MON sẽ
        // hiện nhầm "Đã ĐK môn này" trong khi môn đó không còn lớp nào đang ĐK).
        let next = !canUnblock(p)
            ? p
            : p.iD_lops_TrungLich
                ? { ...p, reasonCode: "SCHEDULE_CONFLICT", message: "Trùng lịch với lớp đã đăng ký." }
                : unblock(p);
        const children = p.listThucHanh;
        if (children?.length) {
            const nextChildren = children.map(ch => (canUnblock(ch) ? unblock(ch) : ch));
            if (nextChildren.some((ch, i) => ch !== children[i])) {
                next = { ...next, listThucHanh: nextChildren };
            }
        }
        return next;
    });
};

/**
 * Pass optimistic cap nhóm tự chọn (đợt chính khoá) — chạy SAU khi các nhánh trùng lịch / khoá nhóm
 * đã set xong. Tính usage theo nhóm từ tập lớp đã ĐK (reasonCode ALREADY_REGISTERED) rồi:
 *   • lớp đang eligible mà nhóm đã đủ → chặn (NHOM_TU_CHON_FULL) → FE ẩn nút.
 *   • lớp đang bị chặn bởi NHOM_TU_CHON_FULL mà nhóm đã hết đủ (do vừa huỷ) → mở lại.
 * Cap chỉ áp khi item mang so_mon_dang_ky/so_tin_chi_tu_chon > 0 (backend chỉ gửi khi đợt chính khoá).
 */
const applyNhomTuChonCap = (list: IClassListItem[]): IClassListItem[] => {
    const registeredIds = new Set<number>();
    for (const c of list) if (c.reasonCode === "ALREADY_REGISTERED") registeredIds.add(c.classId);
    const usage = buildNhomTuChonUsage(list, registeredIds);
    return list.map(c => {
        if (c.canRegister) {
            const block = nhomTuChonBlock(c, usage);
            return block
                ? { ...c, canRegister: false, reasonCode: "NHOM_TU_CHON_FULL", message: block }
                : c;
        }
        if (c.reasonCode === "NHOM_TU_CHON_FULL" && !nhomTuChonBlock(c, usage)) {
            return { ...c, canRegister: true, reasonCode: undefined, message: undefined };
        }
        return c;
    });
};

/**
 * Listen cross-domain registration actions để optimistic-update slotsLeft + trùng lịch
 * → tránh full GET /api/registration/classes mỗi khi register/cancel (50k SV peak = 350k extra calls).
 * Action type loose-typed (any) vì cross-domain — accept cả CLASSES_* và REG_*.
 */
export const classesReducer = (state: IClassesReducer = initialState, action: any): IClassesReducer => {
    switch (action.type) {
        case "CLASSES_LOAD_START":
            return { ...state, status: eReducerStatusBase.is_loading, error_message: "" };
        case "CLASSES_LOAD_SUCCESS":
            return {
                ...state,
                status: eReducerStatusBase.is_completed,
                list: action.payload.list,
                periodId: action.payload.periodId,
            };
        case "CLASSES_LOAD_ERROR":
            return { ...state, status: eReducerStatusBase.is_error, error_message: action.payload };

        // Đổi đợt → xoá list đợt cũ về initial (status is_not_initialization) để effect re-load đợt mới
        // mà không flash data cũ. LOAD_START dispatch ngay sau sẽ chuyển sang is_loading.
        case "CLASSES_RESET":
            return { ...initialState };

        // Bulk update slot từ /api/registration/slots (user chọn môn / click Tải lại).
        // Payload: Record<classId, slotsLeft>. Lớp không có trong payload → giữ nguyên slotsLeft cũ.
        case "CLASSES_UPDATE_SLOTS": {
            const slots: Record<number, number> = action.payload || {};
            return {
                ...state,
                list: state.list.map(c =>
                    slots[c.classId] !== undefined ? { ...c, slotsLeft: slots[c.classId] } : c
                ),
            };
        }

        // Optimistic local cập nhật khi SignalR confirm register cho lớp này:
        //  • lớp vừa register: -1 slot, flag canRegister=false (ALREADY_REGISTERED)
        //  • lớp khác trùng lịch: thêm classId vào iD_lops_TrungLich + flag SCHEDULE_CONFLICT
        // Tránh lỗi UX "đăng ký xong còn thấy lớp trùng vẫn enable, click mới báo trùng".
        case "REG_REGISTER_CONFIRMED": {
            // Multi-period: event của đợt KHÁC đợt đang giữ list (SV đổi đợt trong lúc chờ confirm)
            // → bỏ qua toàn bộ optimistic-update, list đợt mới đã là số liệu server đúng của nó.
            if (isOtherPeriod(state, action)) return state;
            const newRegId: number = action.payload.classId;
            const found = findInList(state.list, newRegId);
            // Lớp thực hành nested: dùng chính nó cho rule 12 (cùng môn), nhưng các nhánh slot/trùng
            // lịch/khoá nhóm bên dưới chỉ áp cho lớp top-level nên registeredClass vẫn là undefined.
            const registeredClass = found && found.parentId === undefined ? found.item : undefined;
            // Khoá nhóm lớp (rule phạm vi chuyên ngành nhóm lớp): đợt bật cờ + lớp vừa ĐK có nhóm (>0)
            // → các lớp khác nhóm bị disable ngay, không cần refetch list. Lớp chung (=0) không khoá.
            const lockNhomLop = !!registeredClass?.is_lock_nhom_lop && (registeredClass?.stu_nhomlop_ids ?? 0) > 0;
            const chosenNhomLopId = registeredClass?.stu_nhomlop_ids ?? 0;
            const mapped = state.list.map((c: IClassListItem) => {
                    if (c.classId === newRegId) {
                        // Chỉ trừ slot khi lớp CHƯA ở trạng thái đã ĐK. reasonCode ALREADY_REGISTERED
                        // nghĩa là slotsLeft đang giữ đã tính lớp này rồi — hoặc do confirm trước đó,
                        // hoặc do list vừa load lại giữa lúc pending (ReserveSlot đã DECR slot + SADD
                        // registered ngay lúc POST). Trừ lần nữa = "Còn trống" hụt 1 tới lần refresh sau.
                        const alreadyCounted = c.reasonCode === "ALREADY_REGISTERED";
                        return {
                            ...c,
                            slotsLeft: alreadyCounted ? c.slotsLeft : Math.max(0, c.slotsLeft - 1),
                            canRegister: false,
                            reasonCode: "ALREADY_REGISTERED",
                            message: "Đã đăng ký lớp này.",
                        };
                    }
                    // Môn chung móc nối CÙNG nhóm lớp: học xen kẽ cùng giờ là hợp lệ → KHÔNG cờ trùng
                    // lịch giả (fix bug "ĐK Đọc-Viết 5 xong không ĐK được Nghe-Nói 5, phải F5"). Nhưng phải
                    // cùng "số lớp" — lệch index thì khoá. Đặt TRƯỚC hasScheduleConflict để partner không
                    // bị cờ trùng. Mirror backend EligibilityService / ClassListService.
                    if (registeredClass && isMonChungPair(c, registeredClass)) {
                        if (c.canRegister && isMonChungIndexMismatch(c, registeredClass)) {
                            return {
                                ...c,
                                canRegister: false,
                                reasonCode: "MON_CHUNG_INDEX_MISMATCH",
                                message: `Môn chung phải đăng ký cùng số lớp — bạn đã chọn lớp ${getLopIndex(registeredClass.ten_lop_hp)}.`,
                            };
                        }
                        return c; // index khớp (hoặc đã khoá vì lý do khác) → giữ nguyên, không báo trùng
                    }
                    if (registeredClass &&
                        hasScheduleConflict(c.thoiKhoaBieus, registeredClass.thoiKhoaBieus)) {
                        const trungIds = parseTrungLichCsv(c.iD_lops_TrungLich);
                        if (!trungIds.includes(newRegId)) trungIds.push(newRegId);
                        return {
                            ...c,
                            iD_lops_TrungLich: toCsv(trungIds),
                            canRegister: false,
                            // Giữ reasonCode/message gốc nếu đã có lý do khác (vd OUT_OF_PHAM_VI),
                            // chỉ set SCHEDULE_CONFLICT khi trước đó là eligible.
                            reasonCode: c.reasonCode ?? "SCHEDULE_CONFLICT",
                            message: c.message ?? "Trùng lịch với lớp đã đăng ký.",
                        };
                    }
                    // Khoá lớp khác nhóm (chỉ lớp đang eligible — giữ reasonCode gốc nếu đã có lý do khác).
                    if (lockNhomLop && c.canRegister
                        && (c.stu_nhomlop_ids ?? 0) > 0 && c.stu_nhomlop_ids !== chosenNhomLopId) {
                        return {
                            ...c,
                            canRegister: false,
                            reasonCode: c.reasonCode ?? "LOCKED_TO_OTHER_NHOM_LOP",
                            message: c.message ?? "Bạn đã chọn nhóm lớp khác trong đợt này — chỉ được đăng ký lớp cùng nhóm.",
                        };
                    }
                    return c;
                });
            // Rule 12 chạy sau map: lớp trùng lịch đã bị khoá ở trên nên giữ SCHEDULE_CONFLICT
            // (+ iD_lops_TrungLich cho nền vàng), chỉ lớp còn eligible mới nhận SAME_MON.
            // found null = lớp không thuộc list đang giữ (đợt khác / đã filter) → bỏ qua.
            return {
                ...state,
                list: applyNhomTuChonCap(
                    found
                        ? applySameMonBlock(mapped, newRegId, found.parentId, sameMonKey(found.item))
                        : mapped
                ),
            };
        }

        // Optimistic local khi cancel — slot trả lại Redis qua RollbackAsync.
        //  • lớp vừa cancel: +1 slot, restore canRegister nếu trước đó chỉ fail vì ALREADY_REGISTERED
        //  • lớp khác: remove cancelledId khỏi iD_lops_TrungLich; nếu hết conflict + reason là
        //    SCHEDULE_CONFLICT (do reducer set trước đó) → restore canRegister.
        case "REG_CANCEL_SUCCESS": {
            // Huỷ ở đợt khác đợt đang giữ list (SV đổi đợt giữa chừng) → bỏ qua optimistic-update.
            if (isOtherPeriod(state, action)) return state;
            // Huỷ cặp LT-TH → nhiều lớp bị huỷ cùng lúc. Restore slot/canRegister cho TẤT CẢ.
            const cancelledIds = new Set<number>(action.payload.cancelledClassIds ?? [action.payload.classId]);
            // Khoá nhóm lớp: chỉ mở lại các lớp bị LOCKED khi SV KHÔNG còn lớp nào đã ĐK có nhóm (>0).
            // Lớp đã ĐK = reasonCode "ALREADY_REGISTERED" (loại các lớp đang huỷ).
            const stillHasNhomRegistered = state.list.some(c =>
                !cancelledIds.has(c.classId)
                && c.reasonCode === "ALREADY_REGISTERED"
                && (c.stu_nhomlop_ids ?? 0) > 0);
            // Rule 12 chiều ngược: môn của các lớp VỪA HUỶ → mở lại lớp khác cùng môn đang bị chặn
            // ALREADY_REGISTERED_SAME_MON, trừ khi môn đó còn lớp khác đang ĐK. Chặn do history /
            // đợt khác thì trong list không có lớp ALREADY_REGISTERED nào cùng khoá → không lọt vào
            // cancelledKeys → giữ nguyên (đúng như backend). Lớp thực hành bị huỷ luôn kèm lớp LT
            // cha (cancelledClassIds cả cặp) nên chỉ cần quét lớp top-level là đủ.
            const cancelledKeys = new Set<string>();
            const stillRegisteredKeys = new Set<string>();
            for (const c of state.list) {
                if (c.reasonCode !== "ALREADY_REGISTERED") continue;
                const key = sameMonKey(c);
                if (key) (cancelledIds.has(c.classId) ? cancelledKeys : stillRegisteredKeys).add(key);
            }
            const restorableKeys = new Set(
                Array.from(cancelledKeys).filter(k => !stillRegisteredKeys.has(k))
            );
            return {
                ...state,
                list: applyNhomTuChonCap(restoreSameMon(state.list.map((c: IClassListItem) => {
                    // Strip cancelledId khỏi iD_lops_TrungLich cho MỌI lớp TRƯỚC khi áp restore.
                    // Nền vàng (hasConflict) phải phản ánh đúng tập lớp còn ĐK ở mọi nhánh — nhánh
                    // LOCKED/MON_CHUNG trước đây return sớm mà không clear iD_lops_TrungLich → bug
                    // "nút đăng ký đã sáng nhưng nền vàng trùng lịch không mất, phải F5".
                    const trungIds = parseTrungLichCsv(c.iD_lops_TrungLich);
                    const newTrung = trungIds.filter(id => !cancelledIds.has(id));
                    const stillConflicted = newTrung.length > 0;
                    const base = newTrung.length !== trungIds.length
                        ? { ...c, iD_lops_TrungLich: toCsv(newTrung) }
                        : c;

                    if (cancelledIds.has(c.classId)) {
                        const wasOnlyAlreadyRegistered = c.reasonCode === "ALREADY_REGISTERED";
                        return {
                            ...base,
                            // Đối xứng với nhánh register: chỉ trả slot khi slotsLeft đang giữ CÓ trừ lớp
                            // này (reasonCode ALREADY_REGISTERED). Cộng vô điều kiện sẽ thừa 1 chỗ khi
                            // huỷ mà FE chưa từng áp -1 (huỷ lúc còn pending, hoặc CANCEL_SUCCESS lặp).
                            slotsLeft: wasOnlyAlreadyRegistered ? c.slotsLeft + 1 : c.slotsLeft,
                            canRegister: wasOnlyAlreadyRegistered ? true : c.canRegister,
                            reasonCode: wasOnlyAlreadyRegistered ? undefined : c.reasonCode,
                            message: wasOnlyAlreadyRegistered ? undefined : c.message,
                        };
                    }
                    // Mở lại lớp bị khoá nhóm khi không còn lớp ĐK có nhóm nào — và đã hết trùng lịch.
                    if (c.reasonCode === "LOCKED_TO_OTHER_NHOM_LOP" && !stillHasNhomRegistered && !stillConflicted) {
                        return { ...base, canRegister: true, reasonCode: undefined, message: undefined };
                    }
                    // Mở lại lớp bị khoá vì lệch "số lớp" môn chung khi không còn partner lệch — và hết trùng lịch.
                    if (c.reasonCode === "MON_CHUNG_INDEX_MISMATCH") {
                        const stillMismatch = state.list.some(r =>
                            !cancelledIds.has(r.classId)
                            && r.reasonCode === "ALREADY_REGISTERED"
                            && isMonChungIndexMismatch(c, r));
                        if (!stillMismatch && !stillConflicted) {
                            return { ...base, canRegister: true, reasonCode: undefined, message: undefined };
                        }
                        return base;
                    }
                    // Lớp bị chặn cùng môn (ALREADY_REGISTERED_SAME_MON) restore ở pass restoreSameMon
                    // sau map — phải chạy ngoài map để phủ cả lớp thực hành nested của lớp vừa huỷ
                    // (nhánh cancelledIds ở trên return sớm, không đụng tới listThucHanh).
                    // Trùng lịch thuần: restore khi hết conflict + reason là SCHEDULE_CONFLICT.
                    // Không restorable → trả base (giữ nguyên ref `c` khi không strip gì → tránh tạo
                    // object thừa cho toàn bộ list mỗi lần huỷ).
                    const restorable = !stillConflicted && c.reasonCode === "SCHEDULE_CONFLICT";
                    if (restorable) {
                        return { ...base, canRegister: true, reasonCode: undefined, message: undefined };
                    }
                    return base;
                }), restorableKeys)),
            };
        }

        default:
            return state;
    }
};
